import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { ModalType } from '../../../app/types';
import {
  deleteDatasets,
  getDatasetListContinue,
  getDatasetListInit,
  getDatasetListTotal,
  refreshDataset,
} from '../../../features/aiBotSlice';
import { closeModal, openModal } from '../../../features/modalSlice';
import useDebounce from '../../../hooks/useDebounce';
import Tabs from '../../shared/Tabs';
import DatasetTable from './DatasetTable';
import Header from './Header';
import NotEmbeddingTip from './NotEmbeddingTip';
import Search from './Search';
import Selected from './Selected';

const useStyles = makeStyles(
  {
    root: {},
  },
  {
    name: 'KnowledgeContainer',
  },
);

const KnowledgeContainer = ({ mainRef, pathKey }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { orgId, id } = useParams();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const { datasets, isLoading, progress } = useSelector(store => store.aiBot);
  const [query, setQuery] = useState({ searchContent: '', status: 'all' });
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedList, setSelectedList] = useState([]);
  const [sort, setSort] = useState({});
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [total, setTotal] = useState(0);
  const [isGettingTotal, setIsGettingTotal] = useState(false);

  const tabs = [
    { key: 'web', name: t(`${pathKey}:web`) },
    { key: 'file', name: t(`${pathKey}:file`) },
  ];

  const dataType = tabs[tabIndex].key;

  const queryRef = useRef(null);
  queryRef.current = { ...query, dataType, sort };

  const onTabIndexChange = (event, index) => {
    setTabIndex(index);
    dispatch(
      getDatasetListInit(orgId, id, {
        ...queryRef.current,
        dataType: tabs[index].key,
      }),
    );
  };

  const dbsHandleSearch = useDebounce(broadcastName => {
    setQuery({ ...query, searchContent: broadcastName });
    dispatch(
      getDatasetListInit(orgId, id, {
        ...queryRef.current,
        searchContent: broadcastName,
      }),
    );
  });

  const onChangeStatus = value => {
    setQuery({ ...query, status: value });
    dispatch(
      getDatasetListInit(orgId, id, {
        ...queryRef.current,
        status: value,
      }),
    );
  };

  const onDelete = v => {
    dispatch(
      openModal({
        modalType: ModalType.ALERT_MODAL,
        modalProps: {
          title: t(`${pathKey}:deleteDataset-modal-title`),
          content: t(`${pathKey}:deleteDataset-modal-content`),
          onCancel: () => {
            dispatch(closeModal());
          },
          onClick: async () => {
            await dispatch(
              deleteDatasets(
                orgId,
                id,
                v?.id ? [v?.id] : selectedList,
                dataType,
                isSelectAll,
              ),
            );
            setSelectedList([]);
            setIsSelectAll(false);
            dispatch(closeModal());
          },
          cancelText: t('modal.cancel'),
          submitText: t('modal.sure'),
        },
      }),
    );
  };

  const onRefresh = async v => {
    if (v?.id) {
      await dispatch(refreshDataset(orgId, id, [v?.id], dataType, isSelectAll));
      await dispatch(
        getDatasetListInit(orgId, id, {
          ...queryRef.current,
          sort: undefined,
        }),
      );

      setSort({});
      setSelectedList([]);
      setIsSelectAll(false);
    } else {
      dispatch(
        openModal({
          modalType: ModalType.ALERT_MODAL,
          modalProps: {
            title: t(`${pathKey}:refreshDataset-modal-title`),
            content: (
              <div
                dangerouslySetInnerHTML={{
                  __html: t(`${pathKey}:refreshDataset-modal-content`),
                }}
              />
            ),
            onCancel: () => {
              dispatch(closeModal());
            },
            onClick: async () => {
              await dispatch(
                refreshDataset(orgId, id, selectedList, dataType, isSelectAll),
              );
              await dispatch(
                getDatasetListInit(orgId, id, {
                  ...queryRef.current,
                  sort: undefined,
                }),
              );

              setSort({});
              setSelectedList([]);
              setIsSelectAll(false);
              dispatch(closeModal());
            },
            cancelText: t('modal.cancel'),
            submitText: t('modal.sure'),
          },
        }),
      );
    }
  };

  const onLazyLoad = async () => {
    const list = await dispatch(
      getDatasetListContinue(orgId, id, queryRef.current),
    );

    if (isSelectAll) {
      setSelectedList(list.map(d => d?.id));
    }
  };

  const onSortChange = v => {
    setSort(v);
    dispatch(
      getDatasetListInit(orgId, id, {
        ...queryRef.current,
        sort: v,
      }),
    );
  };

  const onSelectAll = async v => {
    if (v) {
      if (!isGettingTotal) {
        setIsGettingTotal(true);
        const response = await dispatch(
          getDatasetListTotal(orgId, id, queryRef.current),
        );
        setTotal(response);
        setIsGettingTotal(false);
      }

      setSelectedList(datasets.map(d => d?.id));
    }
    setIsSelectAll(v);
  };

  useEffect(() => {
    if (isSelectAll) {
      setSelectedList(datasets.map(d => d?.id));
    }
  }, [datasets, isSelectAll]);

  useEffect(() => {
    const key = search.get('tab') || 'web';

    setIsSelectAll(false);
    setTabIndex(tabs.findIndex(tab => tab?.key === key));
    dispatch(
      getDatasetListInit(orgId, id, {
        dataType: key,
        status: 'all',
      }),
    );
  }, []);

  return (
    <div className={classes.root}>
      <Header pathKey={pathKey} />
      <NotEmbeddingTip pathKey={pathKey} />
      <Tabs
        className={classes.tabRow}
        value={tabIndex}
        tabs={tabs}
        onChange={onTabIndexChange}
      />
      <Search
        dataType={dataType}
        query={query}
        onChangeSearch={dbsHandleSearch}
        onChangeStatus={onChangeStatus}
      />
      <Selected
        datasets={datasets}
        selectedList={selectedList}
        setSelectedList={v => {
          setSelectedList(v);
        }}
        onDelete={onDelete}
        onRefresh={onRefresh}
        progress={progress}
        dataType={dataType}
        onSelectAll={onSelectAll}
        isSelectAll={isSelectAll}
        total={total}
      />
      <DatasetTable
        dataType={dataType}
        list={datasets}
        selectedList={selectedList}
        setSelectedList={v => {
          setSelectedList(v);
          setIsSelectAll(false);
        }}
        sort={sort}
        setSort={onSortChange}
        onLazyLoad={onLazyLoad}
        onDelete={onDelete}
        onRefresh={onRefresh}
        isLoading={isLoading}
        scrollRef={mainRef}
        progress={progress}
        isSelectAll={isSelectAll}
      />
    </div>
  );
};

export default KnowledgeContainer;
